
import { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { siteboardAction } from '../actions/defaultActions';
import { 
  Email, GET_PROJECT_SCHEDULE_CONTACTS, SET_ALERT, GET_CONTACT_SHARED_SCHEDULE_STATS, SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN,
} from '../actions/types';
import { objectKeysToList, objectValuesToList, getShorthandElapsedSinceTimestamp, } from '../Util';
import { format } from 'date-fns';

import PopupWindow from './PopupWindow';
import PushButton from './PushButton';

// icon imports
import Logo from '../SiteBoard_Logo';

// test cases
const testScrolling = false; // duplicate contacts to see how the UI handles scrolling

const ReviewSharedScheduleViewsDialogue = ({ _projects, _schedules, _subscriptionContacts, _projectsContactEmails, _contactSharedScheduleStats, _reviewSharedScheduleViewsDialogueScheduleId, siteboardAction }) => {

  const selectedSchedule = _schedules[_reviewSharedScheduleViewsDialogueScheduleId];
  const selectedProject = _projects[selectedSchedule.projectId];

  const [scheduleStatsLoaded, setScheduleStatsLoaded] = useState(false);
  const [scheduleContactsLoaded, setScheduleContactsLoaded] = useState(false);

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN, { isOpen: false });
  }, [siteboardAction]);

  // Fetch schedule contacts from the server. If we already have them this will do nothing
  useEffect(() => {
    if(!selectedSchedule) return;
    siteboardAction(GET_CONTACT_SHARED_SCHEDULE_STATS, { projectId: selectedSchedule.projectId, scheduleId: selectedSchedule.id, }, () => {setScheduleStatsLoaded(true); });
    siteboardAction(GET_PROJECT_SCHEDULE_CONTACTS, { projectId: selectedSchedule.projectId }, () => {setScheduleContactsLoaded(true); });
  }, [siteboardAction, selectedSchedule]);

  if(!selectedSchedule || !selectedProject) return (<></>);

  const selectedContacts = (() => {
    if(!(selectedProject.subscriptionId in _subscriptionContacts)) return [];
    const projectContacts = objectKeysToList(_projectsContactEmails[selectedSchedule.projectId] || [])
      .filter((email) => email in _subscriptionContacts[selectedProject.subscriptionId])
      .map((email) => _subscriptionContacts[selectedProject.subscriptionId][email]);
    return projectContacts;
  })();

  selectedContacts.sort((one, two) => {
    const companyCompare = one.companyName.toLowerCase().localeCompare(two.companyName.toLowerCase())
    if(companyCompare !== 0) return companyCompare;
    const lastNameCompare = one.lastName.toLowerCase().localeCompare(two.lastName.toLowerCase())
    if(lastNameCompare !== 0) return lastNameCompare;
    return one.email.toLowerCase().localeCompare(two.email.toLowerCase());
  });

  if(testScrolling) {
    const contacts = [...selectedContacts];
    for(let i = 0; i < 10; i++) {
      selectedContacts.push(...contacts);
    }
  }

  let sharedContactScheduleStatsTotal = 0;
  let contactsViewedScheduleTotal = 0;
  let contactsConfirmedScheduleTotal = 0;
  const viewedScheduleContactEmailsMap = {};
  const lastViewedTimeContactEmailsMap = {};
  for(const scheduleId in _contactSharedScheduleStats) {
    if(scheduleId !== selectedSchedule.id) continue;
    for(const email in _contactSharedScheduleStats[scheduleId]) {
      sharedContactScheduleStatsTotal++;
      if(_contactSharedScheduleStats[scheduleId][email].isViewed) {
        contactsViewedScheduleTotal++;
        viewedScheduleContactEmailsMap[email] = '';
        lastViewedTimeContactEmailsMap[email] = _contactSharedScheduleStats[scheduleId][email].viewedTimestamp;
      }
    }
  }

  const getContactJSX = (contact) => {
    let statusLine = <></>;
    let statusColor = 'border-[#00000000]';
    const latestDeliveryEventTimestamp = (selectedSchedule.projectId in contact.projectScheduleDeliveries) ? contact.projectScheduleDeliveries[selectedSchedule.projectId].timestamp : 0;

    if(contact.hasUnsubscribed) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Opted out, will not receive</div>;
    } else if(contact.hasBounce) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Email address does not exist</div>;
    } else if(contact.hasComplaint) {
      statusColor = 'border-red-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm text-red-500'>Flagged as spam, will not receive</div>;
    } else if(!contact.hasSubscribed && contact.latestInviteEmailTimestamp === 0) {
      // new to subscription
      statusColor = 'border-yellow-500';
      statusLine =  <div className='flex items-center gap-1.5 text-sm opacity-60'>Has not been sent to</div>;
    } else if(!(selectedSchedule.projectId in contact.projectScheduleDeliveries)) {
      // new to project
      statusColor = 'border-yellow-500';
      statusLine = <div className='flex items-center gap-1.5 text-sm opacity-60'>Has not been sent to</div>;
    } else if(!contact.hasSubscribed && contact.latestInviteEmailTimestamp > 0) {
      // awaiting invite acceptance
      statusColor = 'border-yellow-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[550px]:flex-row min-[550px]:items-center min-[550px]:gap-1 text-sm opacity-60'>
            <div>Unviewed, Sent {getShorthandElapsedSinceTimestamp(contact.latestInviteEmailTimestamp)} ago</div>
            <div>, {format(new Date(contact.latestInviteEmailTimestamp), 'MM/dd/yy @ h:mm aa')}</div>
          </div>
        </>
      );
    } else if(contact.hasSubscribed && contact.projectScheduleDeliveries[selectedSchedule.projectId].isDelivered === false) {
      statusColor = 'border-yellow-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[550px]:flex-row min-[550px]:items-center min-[550px]:gap-1 text-sm opacity-60'>
            <div>Queued to Send, {getShorthandElapsedSinceTimestamp(latestDeliveryEventTimestamp)} ago</div>
            {latestDeliveryEventTimestamp > 0 && <div>, {format(new Date(latestDeliveryEventTimestamp), 'MM/dd/yy @ h:mm aa')}</div>}
          </div>
        </>
      );
    } else if(contact.hasSubscribed && contact.projectScheduleDeliveries[selectedSchedule.projectId].isDelivered === true && !(contact.email in viewedScheduleContactEmailsMap)) {
      statusColor = 'border-yellow-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[550px]:flex-row min-[550px]:items-center min-[550px]:gap-1 text-sm opacity-60'>
            <div>Unviewed, Sent {getShorthandElapsedSinceTimestamp(latestDeliveryEventTimestamp)} ago</div>
            <div>, {format(new Date(latestDeliveryEventTimestamp), 'MM/dd/yy @ h:mm aa')}</div>
          </div>
        </>
      );
    } else if(contact.hasSubscribed && contact.projectScheduleDeliveries[selectedSchedule.projectId].isDelivered === true && contact.email in viewedScheduleContactEmailsMap) {
      statusColor = 'border-green-500';
      statusLine = (
        <>
          <div className='flex flex-col items-start min-[370px]:flex-row min-[370px]:items-center min-[370px]:gap-1 text-sm opacity-60'>
            <div>Viewed {getShorthandElapsedSinceTimestamp(lastViewedTimeContactEmailsMap[contact.email])} ago</div>
            {<div>, {format(new Date(lastViewedTimeContactEmailsMap[contact.email]), 'MM/dd/yy @ h:mm aa')}</div>}
          </div>
        </>
      );
    }

    return (
      <div 
        key={contact.email} 
        className='flex justify-between items-center px-3 py-1.5 gap-2 hover:bg-neutral-800'
      >
        <div className={`flex flex-col grow-[1] border-l-4 ${statusColor} pl-2`}>
          {contact.companyName.length > 0 && <div className='flex items-center gap-1.5'>{contact.companyName}</div>}
          {(contact.firstName.length > 0 || contact.lastName.length > 0) && <div className='flex items-center gap-1.5'>{contact.firstName} {contact.lastName}</div>}
          <div className='break-all'>{contact.email}</div>
          {statusLine}
        </div>
      </div>
    );
  };

  return (
    <PopupWindow 
      title={'Schedule Views'}
      closeDialogue={closeDialogue} 
      bgStyle='bg-[#333333]'
    >
      <div className={`flex flex-col gap-2 max-w-screen-lg text-white`}>
        <div className='w-[90vw] max-w-full' />
        {!scheduleContactsLoaded || !scheduleStatsLoaded ?
          <div className={`grow flex flex-col justify-center gap-y-1 p-6`}>
            <div className='flex rounded-lg py-6 px-10 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
              <div className='animate-[spin_1s_linear_infinite]'>
                <Logo size={48} />
              </div>
              <p className='select-none'>Loading Contacts</p>
            </div>
          </div>
        :
          <>
            <div className='opacity-70'>Distribution list views for <b>{selectedProject.name}</b>, schedule starting <b>{selectedSchedule.beginDate}</b>, published on <b>{format(selectedSchedule.publishedTimestamp, 'MM/dd/yyyy')}</b>.</div>
            <div className='text-[1.2em]'><b>{contactsViewedScheduleTotal}</b> Viewed</div>
            <div className='bg-neutral-700 rounded-lg py-2'>
              {selectedContacts.filter(contact => contact.email in viewedScheduleContactEmailsMap).map((contact, index) => (
                getContactJSX(contact)
              ))}
            </div>

            <div className='text-[1.2em] mt-6'><b>{sharedContactScheduleStatsTotal - contactsViewedScheduleTotal}</b> Haven't Viewed</div>
            <div className='bg-neutral-700 rounded-lg py-2'>
              {selectedContacts.filter(contact => !(contact.email in viewedScheduleContactEmailsMap)).map((contact, index) => (
                getContactJSX(contact)
              ))}
            </div>
          </>
        }
      </div>
    </PopupWindow>
  );
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return {
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _subscriptionContacts: state.serverSynced.subscriptionContacts,
  _projectsContactEmails: state.serverSynced.projectsContactEmails,
  _contactSharedScheduleStats: state.serverSynced.contactSharedScheduleStats,
  _reviewSharedScheduleViewsDialogueScheduleId: state.localEphemeral.reviewSharedScheduleViewsDialogueScheduleId,
}};
export default connect(mapStateToProps, { siteboardAction })(ReviewSharedScheduleViewsDialogue);
